import { ApiError, notifyUnauthorized } from './client'

const BASE = '/admin'

export interface ModelProperty {
  name: string
  type: string | null
  label: string
  pluralLabel?: string
}
export interface ContentModel {
  id: string
  label: string
  pluralLabel?: string
  readOnly?: boolean
  single?: boolean
  properties: ModelProperty[]
}

export interface ContentEntity {
  id: string
  modelId: string
  elements: Record<string, unknown>
  // Yep serializes these as ISO-8601 strings (e.g. "2026-06-05T12:24:39Z"); numbers appear on some models.
  creationDate?: number | string
  lastUpdate?: number | string
  publishingStatus?: string
}
export interface ContentListResult {
  data: ContentEntity[]
  totalCount: number
}

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
  })
  if (res.status === 401) notifyUnauthorized()
  if (!res.ok) throw new ApiError(`HTTP ${res.status}`, res.status)
  const text = await res.text()
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError('Invalid JSON', res.status)
  }
}

/** All content model definitions (field labels/types). Cached by the query layer. */
export function getContentModels(): Promise<ContentModel[]> {
  return getJson<ContentModel[]>(`${BASE}/rest/model/content`)
}

export interface ListOpts {
  offset?: number
  limit?: number
  language?: string
  /** SQL-style WHERE expression, e.g. `status = 'CONFIRMED' and transport = '1'`. */
  filter?: string
  /** e.g. `arrivalDate desc`. */
  sort?: string
}

/** A paged content list. CRITICAL: always pass a limit — the API default is ~1e9 (OOM). */
export function getContentList(model: string, opts: ListOpts = {}): Promise<ContentListResult> {
  const p = new URLSearchParams({
    language: opts.language ?? 'it',
    offset: String(opts.offset ?? 0),
    limit: String(opts.limit ?? 50),
  })
  if (opts.filter) p.set('filter', opts.filter)
  if (opts.sort) p.set('sort', opts.sort)
  return getJson<ContentListResult>(`${BASE}/rest/content/${model}?${p.toString()}`)
}

/** A single content entity, fully populated (all tabs/elements) — needed before saving. */
export function getContent(model: string, id: string, language = 'it'): Promise<ContentEntity> {
  return getJson<ContentEntity>(`${BASE}/rest/content/${model}/${id}?language=${language}`)
}

/**
 * Save a content entity. Mirrors the legacy `saveContent`: POST the FULL object to
 * /rest/content/{model}/{id}. ALWAYS round-trip — load via getContent(), mutate only the
 * target elements, then save — so Validity/Overrides/translations are preserved untouched.
 */
export async function saveContent(model: string, content: ContentEntity, language = 'it'): Promise<ContentEntity> {
  const url = content.id ? `${BASE}/rest/content/${model}/${content.id}` : `${BASE}/rest/content/${model}`
  // ContentSaveAction reads $data->language directly (no fallback on the content-load path),
  // so the body MUST carry `language`, plus `elements` (and `meta` for meta-enabled models).
  const body = { ...content, language }
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (res.status === 401) notifyUnauthorized()
  if (!res.ok) {
    const raw = await res.text().catch(() => '')
    let msg = `HTTP ${res.status}`
    try {
      const j = JSON.parse(raw) as { message?: string }
      if (j?.message) msg = j.message
    } catch {
      if (raw) msg = `HTTP ${res.status}: ${raw.slice(0, 200)}`
    }
    throw new ApiError(msg, res.status)
  }
  const text = await res.text()
  try {
    return JSON.parse(text) as ContentEntity
  } catch {
    return content
  }
}

/** Excel export URL (server caps at 250 rows). Honors the same filter/sort. */
export function exportListUrl(model: string, opts: { filter?: string; sort?: string } = {}): string {
  const p = new URLSearchParams({ model })
  if (opts.filter) p.set('filter', opts.filter)
  if (opts.sort) p.set('sort', opts.sort)
  return `${BASE}/actions/exportList?${p.toString()}`
}
